// v0.4.45 — Amendments inbox page. Reachable at /amendments.
// Status filter, overdue toggle, and a row list rendered by
// AmendmentRow.
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { AmendmentRow } from "../components/AmendmentRow";
import { useAmendmentsList } from "../hooks/useAmendments";
import { useTranslation } from "react-i18next";

const STATUSES = ["OPEN", "SUBMITTED", "RESOLVED"];

export default function Amendments() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const focusedId = searchParams.get("focus");
  const nav = useNavigate();
  const [status, setStatus] = useState("OPEN");
  const [overdue, setOverdue] = useState(false);
  const [allMode, setAllMode] = useState(!!focusedId);
  const { data, loading, error, refresh } = useAmendmentsList({
    status, overdue: overdue ? true : undefined,
  });

  const showRows = data.amendments;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50">
      <Header />
      <main className="mx-auto max-w-5xl px-6 py-8">
        <nav data-testid="amendments-breadcrumb" className="flex items-center gap-2 text-xs tracking-normal text-slate-500 dark:text-slate-400 mb-4">
          <button
            data-testid="amendments-back-dashboard"
            onClick={() => nav("/")}
            className="text-teal-700 dark:text-teal-300 hover:text-teal-700 dark:hover:text-teal-300 underline"
          >
            {t("ui.amendments.breadcrumb_dashboard", { defaultValue: "← Dashboard" })}
          </button>
          <span className="text-slate-500 dark:text-slate-400">/</span>
          <span className="text-slate-600 dark:text-slate-300">{t("ui.amendments.breadcrumb_amendments", { defaultValue: "Amendments" })}</span>
        </nav>
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
              {t("ui.amendments.eyebrow", { defaultValue: "Compliance · Amendments" })}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
              {t("ui.amendments.title", { defaultValue: "Pending corrections" })}
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 max-w-2xl">
              {t("ui.amendments.intro", { defaultValue: "Every amendment is an obligation against a frozen filing. The audit trail records every state change and operator decision — forever." })}
            </p>
          </div>
          <button
            data-testid="amendments-refresh"
            onClick={() => { setAllMode(false); refresh(); }}
            className="border border-slate-300 dark:border-slate-700 hover:border-teal-500 dark:hover:border-teal-400 text-slate-700 dark:text-slate-200 hover:text-teal-700 dark:hover:text-teal-300 dark:text-teal-300 px-3 py-1.5 rounded-full text-xs font-medium"
          >
            {t("ui.amendments.refresh", { defaultValue: "Refresh" })}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-xs text-slate-600 dark:text-slate-300 font-medium mr-1">
            {t("ui.amendments.status_label", { defaultValue: "Status:" })}
          </span>
          {STATUSES.map((s) => {
            const active = !allMode && status === s;
            return (
              <button
                key={s}
                data-testid={`amendments-filter-${s.toLowerCase()}`}
                onClick={() => { setAllMode(false); setStatus(s); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                  active
                    ? "bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                    : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-500"
                }`}
              >
                {s}
              </button>
            );
          })}
          <span className="text-slate-400 dark:text-slate-500 mx-2">·</span>
          <label className="text-xs flex items-center gap-2 text-slate-600 dark:text-slate-300 select-none">
            <input
              data-testid="amendments-overdue-toggle"
              type="checkbox"
              checked={overdue}
              onChange={(e) => { setAllMode(false); setOverdue(e.target.checked); }}
            />
            {t("ui.amendments.overdue_only", { defaultValue: "Overdue only (4-yr bar)" })}
          </label>
          {allMode && (
            <span className="text-xs text-amber-700 dark:text-amber-300 font-medium ml-auto">
              {t("ui.amendments.showing_all", { defaultValue: "Showing all (deep-link)" })}
            </span>
          )}
        </div>

        {loading && (
          <div data-testid="amendments-loading" className="text-slate-500 dark:text-slate-400 text-sm">
            {t("ui.admin_clients.loading", { defaultValue: "Loading…" })}
          </div>
        )}
        {error && !loading && (
          <div data-testid="amendments-error" className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-lg px-3 py-2 mb-3">
            {error}
          </div>
        )}
        {!loading && !error && showRows.length === 0 && (
          <div
            data-testid="amendments-empty"
            className="border border-slate-200 dark:border-slate-700 rounded-2xl bg-slate-50 dark:bg-slate-950 px-5 py-10 text-center text-slate-500 dark:text-slate-400"
          >
            {t("ui.amendments.empty", { defaultValue: "No amendments match the current filter." })}
          </div>
        )}

        <div>
          {showRows.map((a) => (
            <div
              key={a.id}
              ref={(el) => {
                if (el && focusedId === a.id) {
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              }}
              className={focusedId === a.id ? "ring-2 ring-amber-400/60 rounded-2xl" : ""}
            >
              <AmendmentRow amendment={a} onChanged={refresh} />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
